/**
 * Página /superadmin/cargas-recursos — Sprint 19 S19-B4.
 *
 * Carga inicial pre-LIVE de recursos físicos ya entregados a las casas:
 * llaveros (con serial RFID) y controles remotos. Dos pestañas, cada una
 * con el patrón de carga sa (igual que Pagos iniciales): pega o sube el CSV,
 * "Vista previa" (dry-run, valida sin persistir), y "Confirmar carga".
 *
 * El backend siembra cada fila como Recurso `entregado` `carga_inicial=true`
 * (los llaveros además guardan el serial en un StockRecurso asignado). La
 * carga es idempotente: re-correr un archivo omite lo ya cargado.
 */
import { useRef, useState, type ChangeEvent } from 'react';
import { ApiError } from '../../lib/api/client';
import {
  cargarControlesCsv,
  cargarLlaverosCsv,
  type CargaCsvPayload,
  type CargaResultado,
} from '../../lib/api/cargas';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/layout/PageHeader';

type Tab = 'llaveros' | 'controles';

interface TabConfig {
  titulo: string;
  descripcion: string;
  plantilla: string;
  nombrePlantilla: string;
  placeholder: string;
  cargar: (payload: CargaCsvPayload) => Promise<CargaResultado>;
}

const CONFIG: Record<Tab, TabConfig> = {
  llaveros: {
    titulo: 'Llaveros',
    descripcion:
      'Cada fila es un llavero ya entregado a una casa. La columna serial_rfid es el número de serie del proveedor; etiqueta es el rótulo LL-NNNN. Idempotente por serial.',
    plantilla: 'casa,serial_rfid,etiqueta,fecha_entrega\n6931,1234567890,LL-0001,2026-01-15\n',
    nombrePlantilla: 'plantilla-llaveros-calbucura.csv',
    placeholder:
      'casa,serial_rfid,etiqueta,fecha_entrega\n6931,1234567890,LL-0001,2026-01-15',
    cargar: cargarLlaverosCsv,
  },
  controles: {
    titulo: 'Controles remotos',
    descripcion:
      'Cada fila es un control remoto ya entregado a una casa. La columna codigo es su identificador (CR-<casa>-NN). No lleva serial. Idempotente por código.',
    plantilla: 'casa,codigo,fecha_entrega\n6931,CR-6931-01,2026-01-15\n',
    nombrePlantilla: 'plantilla-controles-calbucura.csv',
    placeholder: 'casa,codigo,fecha_entrega\n6931,CR-6931-01,2026-01-15',
    cargar: cargarControlesCsv,
  },
};

export function CargasRecursosPage() {
  const [tab, setTab] = useState<Tab>('llaveros');

  return (
    <div className="px-8 py-6 space-y-6">
      <PageHeader
        eyebrow="Carga inicial LIVE"
        title="Recursos entregados"
        description="Importador CSV de llaveros y controles remotos ya entregados a las casas antes del arranque del sistema. Usa 'Vista previa' para validar sin persistir."
      />

      <div className="flex gap-2 border-b-[0.5px] border-white/[0.08]">
        {(Object.keys(CONFIG) as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-[0.5px] transition-colors ${
              tab === t
                ? 'border-admin-red text-text-primary'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            {CONFIG[t].titulo}
          </button>
        ))}
      </div>

      {/* key fuerza remount al cambiar de tab → limpia CSV y reporte. */}
      <CargaForm key={tab} config={CONFIG[tab]} />
    </div>
  );
}

function CargaForm({ config }: { config: TabConfig }) {
  const [csv, setCsv] = useState('');
  const [resultado, setResultado] = useState<CargaResultado | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  function descargarPlantilla() {
    const blob = new Blob([config.plantilla], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = config.nombrePlantilla;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function handleArchivo(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsv(await file.text());
    setResultado(null);
    setError(null);
  }

  async function ejecutar(dryRun: boolean) {
    if (!csv.trim()) {
      setError('Pega o sube un CSV antes de continuar.');
      return;
    }
    setSubmitting(true);
    setError(null);
    setResultado(null);
    try {
      setResultado(await config.cargar({ csv_contenido: csv, dry_run: dryRun }));
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-3 max-w-4xl">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-text-secondary max-w-3xl">
          {config.descripcion}
        </p>
        <Button variant="ghost" onClick={descargarPlantilla}>
          Descargar plantilla
        </Button>
      </div>

      <label className="text-sm space-y-1 block">
        <span className="text-text-secondary">
          Contenido CSV (pega o sube archivo)
        </span>
        <textarea
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
          rows={10}
          placeholder={config.placeholder}
          className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary font-mono text-xs"
        />
      </label>

      <div className="flex flex-wrap gap-2 items-center">
        <input
          type="file"
          accept=".csv,text/csv"
          ref={fileInputRef}
          onChange={handleArchivo}
          className="hidden"
        />
        <Button
          variant="ghost"
          onClick={() => fileInputRef.current?.click()}
          disabled={submitting}
        >
          Subir archivo CSV
        </Button>
        <Button
          variant="ghost"
          onClick={() => ejecutar(true)}
          disabled={submitting || !csv.trim()}
        >
          {submitting ? 'Procesando…' : 'Vista previa (dry-run)'}
        </Button>
        <Button
          variant="primary"
          onClick={() => ejecutar(false)}
          disabled={submitting || !csv.trim()}
        >
          {submitting ? 'Procesando…' : 'Confirmar carga'}
        </Button>
      </div>

      {error && (
        <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
          {error}
        </div>
      )}

      {resultado && <ResultadoCarga resultado={resultado} />}
    </div>
  );
}

function ResultadoCarga({ resultado }: { resultado: CargaResultado }) {
  const noCreados = resultado.filas.filter((f) => f.resultado !== 'creado');
  const banner =
    resultado.modo === 'real' && resultado.creados > 0
      ? 'border-state-success bg-state-success/10'
      : 'border-admin-red bg-bg-elevated';

  return (
    <div className="space-y-4">
      <div className={`border-l-4 rounded-sm p-4 ${banner} text-text-primary`}>
        <div className="text-xs uppercase tracking-wide font-medium mb-2">
          {resultado.modo === 'dry_run'
            ? '🔍 Vista previa (no se persistió nada)'
            : '✅ Carga confirmada'}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 text-sm">
          <Metrica label="Filas" value={resultado.total} />
          <Metrica label="Creados" value={resultado.creados} />
          <Metrica label="Omitidos" value={resultado.omitidos} />
          <Metrica
            label="Errores"
            value={resultado.errores}
            highlight={resultado.errores > 0}
          />
        </div>
      </div>

      {noCreados.length > 0 && (
        <div className="bg-bg-elevated border-l-4 border-white/[0.12] rounded-sm p-3 text-sm">
          <div className="font-medium text-text-primary mb-1">
            Filas no creadas ({noCreados.length}):
          </div>
          <ul className="space-y-0.5 text-text-secondary text-xs max-h-60 overflow-y-auto">
            {noCreados.map((f) => (
              <li key={f.fila}>
                Fila {f.fila} ({f.referencia}
                {f.casa ? `, casa ${f.casa}` : ''}):{' '}
                <span
                  className={
                    f.resultado === 'error'
                      ? 'text-state-danger'
                      : 'text-text-muted'
                  }
                >
                  {f.resultado === 'omitido' ? 'omitido' : 'error'}
                </span>
                {f.detalle ? ` — ${f.detalle}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Metrica({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: number;
  highlight?: boolean;
}) {
  return (
    <div>
      <div className="text-xs text-text-muted">{label}</div>
      <div
        className={`text-lg font-medium ${
          highlight && value > 0 ? 'text-state-danger' : 'text-text-primary'
        }`}
      >
        {value}
      </div>
    </div>
  );
}
